import { create } from 'zustand'
import type { Room, Member, AvailableActionOption } from '../types/game'

export type AppScreen = 'menu' | 'waiting' | 'game'

export interface GameEvent {
  seq: number
  type: string
  data: Record<string, unknown>
  timestamp: number
}

export interface UIState {
  // Identity
  memberId: string
  sessionId: string
  alias: string

  // Current room
  roomId: string | null
  room: Room | null
  member: Member | null

  // Lobby room list (server-pushed, no polling)
  lobbyRooms: Room[]
  lobbyRoomsLoaded: boolean

  // Available actions (cached)
  availableActions: AvailableActionOption[]

  // Events log (incremental)
  events: GameEvent[]

  // UI state
  screen: AppScreen
  errorMessage: string | null
  isLoading: boolean

  // Modals
  showQueenTrading: boolean
  showSeasonOverview: boolean
  showDonationModal: boolean
  showDonationResponse: boolean
  showColonyCollapse: boolean
  showColonyCollapseOverview: boolean
  showGameEnd: boolean
  showGameMenu: boolean
  showHostModal: boolean
  showJoinModal: boolean

  // Pending follow-ups
  pendingSeasonOverview: boolean
  dismissedSeasonOverviewSeq: number | null
  dismissedColonyCollapseOverviewSeq: number | null
  animationBlockUntil: number
  soundEnabled: boolean
  musicEnabled: boolean
  soundVolume: number
  musicVolume: number
  botThinkingTime: number
}

export interface GameActions {
  setMemberId: (id: string) => void
  setSessionId: (id: string) => void
  setAlias: (alias: string) => void
  setRoomId: (id: string | null) => void
  setRoom: (room: Room | null) => void
  setMember: (member: Member | null) => void
  setLobbyRooms: (rooms: Room[]) => void
  setLobbyRoomsLoaded: (loaded: boolean) => void
  setAvailableActions: (actions: AvailableActionOption[]) => void
  appendEvents: (events: GameEvent[]) => void
  setScreen: (screen: AppScreen) => void
  setError: (message: string | null) => void
  setLoading: (loading: boolean) => void
  setShowQueenTrading: (show: boolean) => void
  setShowSeasonOverview: (show: boolean) => void
  setShowDonationModal: (show: boolean) => void
  setShowDonationResponse: (show: boolean) => void
  setShowColonyCollapse: (show: boolean) => void
  setShowColonyCollapseOverview: (show: boolean) => void
  setShowGameEnd: (show: boolean) => void
  setShowGameMenu: (show: boolean) => void
  setShowHostModal: (show: boolean) => void
  setShowJoinModal: (show: boolean) => void
  setPendingSeasonOverview: (pending: boolean) => void
  setDismissedSeasonOverviewSeq: (seq: number | null) => void
  setDismissedColonyCollapseOverviewSeq: (seq: number | null) => void
  setAnimationBlockUntil: (timestamp: number) => void
  setSoundEnabled: (enabled: boolean) => void
  setMusicEnabled: (enabled: boolean) => void
  setSoundVolume: (volume: number) => void
  setMusicVolume: (volume: number) => void
  setBotThinkingTime: (seconds: number) => void
}

const MEMBER_ID_PATTERN = /^[a-z]{3}-[a-z]{3}-[a-z]{3}$/

function generateMemberId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz'
  const chunk = () =>
    Array.from({ length: 3 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
  return `${chunk()}-${chunk()}-${chunk()}`
}

function normalizeMemberId(value: string | null): string {
  if (value && MEMBER_ID_PATTERN.test(value)) {
    return value
  }
  return generateMemberId()
}

// Generate a UUID v4
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// Load persistent identity from localStorage
function loadIdentity(): { memberId: string; alias: string } {
  const memberId = normalizeMemberId(localStorage.getItem('cc_member_id'))
  const alias = localStorage.getItem('cc_alias') || `Player${memberId.slice(0, 4)}`
  localStorage.setItem('cc_member_id', memberId)
  localStorage.setItem('cc_alias', alias)
  return { memberId, alias }
}

function loadBooleanSetting(key: string, fallback: boolean): boolean {
  const value = localStorage.getItem(key)
  if (value === null) return fallback
  return value === 'true'
}

function loadVolumeSetting(key: string, fallback: number): number {
  const rawValue = localStorage.getItem(key)
  if (rawValue === null) return fallback
  const value = Number(rawValue)
  if (!Number.isFinite(value)) return fallback
  return Math.max(0, Math.min(1, value))
}

function loadBotThinkingTimeSetting(fallback: number): number {
  const rawValue = localStorage.getItem('cc_bot_thinking_time')
  if (rawValue === null) return fallback
  const value = Number(rawValue)
  if (!Number.isFinite(value)) return fallback
  return Math.max(0, Math.min(5, value))
}

const { memberId, alias } = loadIdentity()
const sessionId = generateUUID()

export const useGameStore = create<UIState & GameActions>((set) => ({
  // Identity
  memberId,
  sessionId,
  alias,

  // Room state
  roomId: localStorage.getItem('cc_room_id'),
  room: null,
  member: null,
  lobbyRooms: [],
  lobbyRoomsLoaded: false,

  // Game state
  availableActions: [],
  events: [],

  // UI
  screen: 'menu',
  errorMessage: null,
  isLoading: false,

  // Modals
  showQueenTrading: false,
  showSeasonOverview: false,
  showDonationModal: false,
  showDonationResponse: false,
  showColonyCollapse: false,
  showColonyCollapseOverview: false,
  showGameEnd: false,
  showGameMenu: false,
  showHostModal: false,
  showJoinModal: false,
  pendingSeasonOverview: false,
  dismissedSeasonOverviewSeq: null,
  dismissedColonyCollapseOverviewSeq: null,
  animationBlockUntil: 0,
  soundEnabled: loadBooleanSetting('cc_sound_enabled', true),
  musicEnabled: loadBooleanSetting('cc_music_enabled', true),
  soundVolume: loadVolumeSetting('cc_sound_volume', 0.75),
  musicVolume: loadVolumeSetting('cc_music_volume', 0.55),
  botThinkingTime: loadBotThinkingTimeSetting(3),

  // Actions
  setMemberId: (id) => {
    localStorage.setItem('cc_member_id', id)
    set({ memberId: id })
  },
  setSessionId: (id) => set({ sessionId: id }),
  setAlias: (alias) => {
    localStorage.setItem('cc_alias', alias)
    set({ alias })
  },
  setRoomId: (id) => {
    if (id) {
      localStorage.setItem('cc_room_id', id)
    } else {
      localStorage.removeItem('cc_room_id')
    }
    set({ roomId: id })
  },
  setLobbyRooms: (rooms) => set({ lobbyRooms: rooms, lobbyRoomsLoaded: true }),
  setLobbyRoomsLoaded: (loaded) => set({ lobbyRoomsLoaded: loaded }),
  setRoom: (room) => {
    if (room) {
      let screen: AppScreen
      if (room.is_lobby) {
        screen = 'menu'
      } else if (room.game !== null) {
        screen = 'game'
      } else if (room.in_lobby) {
        screen = 'waiting'
      } else {
        screen = 'menu'
      }
      set({ room, screen })
    } else {
      set({ room, screen: 'menu' })
    }
  },
  setMember: (member) => set({ member }),
  setAvailableActions: (actions) => set({ availableActions: Array.isArray(actions) ? actions : [] }),
  appendEvents: (newEvents) =>
    set((state) => ({ events: [...state.events.slice(-200), ...newEvents] })),
  setScreen: (screen) => set({ screen }),
  setError: (message) => set({ errorMessage: message }),
  setLoading: (loading) => set({ isLoading: loading }),
  setShowQueenTrading: (show) => set({ showQueenTrading: show }),
  setShowSeasonOverview: (show) => set({ showSeasonOverview: show }),
  setShowDonationModal: (show) => set({ showDonationModal: show }),
  setShowDonationResponse: (show) => set({ showDonationResponse: show }),
  setShowColonyCollapse: (show) => set({ showColonyCollapse: show }),
  setShowColonyCollapseOverview: (show) => set({ showColonyCollapseOverview: show }),
  setShowGameEnd: (show) => set({ showGameEnd: show }),
  setShowGameMenu: (show) => set({ showGameMenu: show }),
  setShowHostModal: (show) => set({ showHostModal: show }),
  setShowJoinModal: (show) => set({ showJoinModal: show }),
  setPendingSeasonOverview: (pending) => set({ pendingSeasonOverview: pending }),
  setDismissedSeasonOverviewSeq: (seq) => set({ dismissedSeasonOverviewSeq: seq }),
  setDismissedColonyCollapseOverviewSeq: (seq) => set({ dismissedColonyCollapseOverviewSeq: seq }),
  setAnimationBlockUntil: (timestamp) => set({ animationBlockUntil: timestamp }),
  setSoundEnabled: (enabled) => {
    localStorage.setItem('cc_sound_enabled', String(enabled))
    set({ soundEnabled: enabled })
  },
  setMusicEnabled: (enabled) => {
    localStorage.setItem('cc_music_enabled', String(enabled))
    set({ musicEnabled: enabled })
  },
  setSoundVolume: (volume) => {
    const normalized = Math.max(0, Math.min(1, volume))
    localStorage.setItem('cc_sound_volume', String(normalized))
    set({ soundVolume: normalized })
  },
  setBotThinkingTime: (seconds) => {
    const normalized = Math.max(0, Math.min(5, seconds))
    localStorage.setItem('cc_bot_thinking_time', String(normalized))
    set({ botThinkingTime: normalized })
  },
  setMusicVolume: (volume) => {
    const normalized = Math.max(0, Math.min(1, volume))
    localStorage.setItem('cc_music_volume', String(normalized))
    set({ musicVolume: normalized })
  },
}))
